import { Beaker, Minus, TrendingDown, TrendingUp } from "lucide-react";
import type * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, cn } from "@ando/ui";

import type { HypermemExperiment } from "@ando/core";

import { useHypermemExperiments } from "@/features/admin/hooks/useAdminHypermem";
import { formatInt, formatPct, formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * HyperMem A/B experiments tab (v2 port of v1 ``ExperimentsTab``, Jun 6, 2026).
 *
 * Live view of the prompt experiments orchestrated by the MetaPrompt agent.
 * The agent compares prompt versions via a two-proportion chi-square test
 * (α=0.05, effect ≥ 5%); the backend EXPERIMENT_MONITOR job auto-promotes the
 * winner once both significance and effect-size thresholds are met. The chi²
 * computed here is a client-side *preview* only — the backend is authoritative.
 *
 * Data: ``useHypermemExperiments(tenantId)`` → ``{ experiments }``. Aggregate
 * counters only (no PII).
 */

const CHI_SQ_CRITICAL_005 = 3.841; // df=1, α=0.05
const EFFECT_THRESHOLD = 0.05; // 5% absolute effect size

/** success / total with a divide-by-zero guard. */
function successRate(success: number, total: number): number {
  return total > 0 ? success / total : 0;
}

/**
 * Traffic split fields are expressed as percentage points (0–100) by the BE
 * (``*_traffic_pct``). Normalise to a 0–1 fraction for display, tolerating the
 * occasional 0–1 value defensively.
 */
function trafficFraction(pct: number): number {
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return pct > 1 ? pct / 100 : pct;
}

/**
 * Two-proportion chi-square test (one degree of freedom). Returns the χ²
 * statistic, or null when there are not yet enough samples to compute it.
 */
function chiSquare(cSucc: number, cTot: number, tSucc: number, tTot: number): number | null {
  if (cTot <= 0 || tTot <= 0) return null;
  const total = cTot + tTot;
  const succTotal = cSucc + tSucc;
  const failTotal = total - succTotal;
  if (succTotal === 0 || failTotal === 0) return null;

  const expCSucc = (cTot * succTotal) / total;
  const expCFail = (cTot * failTotal) / total;
  const expTSucc = (tTot * succTotal) / total;
  const expTFail = (tTot * failTotal) / total;

  const cFail = cTot - cSucc;
  const tFail = tTot - tSucc;

  const term = (obs: number, exp: number) => (exp > 0 ? (obs - exp) ** 2 / exp : 0);

  return (
    term(cSucc, expCSucc) + term(cFail, expCFail) + term(tSucc, expTSucc) + term(tFail, expTFail)
  );
}

function SignificancePrediction({
  cSucc,
  cTot,
  tSucc,
  tTot,
  effect,
}: {
  cSucc: number;
  cTot: number;
  tSucc: number;
  tTot: number;
  effect: number;
}): React.ReactElement {
  const chi = chiSquare(cSucc, cTot, tSucc, tTot);
  const effectAbs = Math.abs(effect);

  if (chi === null) {
    return (
      <span className="text-ink-3">
        <Trans>in attesa di campioni</Trans>
      </span>
    );
  }

  const significant = chi >= CHI_SQ_CRITICAL_005;
  const effectMet = effectAbs >= EFFECT_THRESHOLD;

  if (significant && effectMet) {
    return (
      <span className="font-medium text-emerald-600">
        <Trans>raggiunto (χ² {chi.toFixed(2)})</Trans>
      </span>
    );
  }
  if (significant && !effectMet) {
    return (
      <span className="text-amber-600">
        <Trans>significativo, effect &lt;5% (χ² {chi.toFixed(2)})</Trans>
      </span>
    );
  }
  if (chi >= 2) {
    return (
      <span className="text-amber-600">
        <Trans>presto significativo (χ² {chi.toFixed(2)})</Trans>
      </span>
    );
  }
  return (
    <span className="text-ink-3">
      <Trans>ancora non significativo (χ² {chi.toFixed(2)})</Trans>
    </span>
  );
}

function ArmCard({
  title,
  rate,
  samples,
  traffic,
  highlight,
}: {
  title: string;
  rate: number;
  samples: number;
  traffic: number;
  highlight?: boolean;
}): React.ReactElement {
  const ratePct = rate * 100;
  return (
    <div
      className={cn(
        "rounded-xl border p-3 transition-colors",
        highlight ? "border-emerald-500/50 bg-emerald-500/5" : "border-hairline bg-cream/40",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-ink-3 text-xs uppercase tracking-wide">{title}</span>
        <span className="text-[11px] text-ink-3">
          <Trans>{formatPct(traffic * 100, 0)} traffic</Trans>
        </span>
      </div>
      <p className="mt-1 font-semibold text-ink-1 text-xl tabular-nums">{formatPct(ratePct, 2)}</p>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink-3/10">
        <div
          className={cn("h-full rounded-full", highlight ? "bg-emerald-500" : "bg-ando-orange/70")}
          style={{ width: `${Math.min(100, Math.max(0, ratePct))}%` }}
        />
      </div>
      <p className="mt-1.5 text-[11px] text-ink-3 tabular-nums">
        <Trans>{formatInt(samples)} campioni</Trans>
      </p>
    </div>
  );
}

function ExperimentCard({
  exp,
}: {
  exp: HypermemExperiment;
}): React.ReactElement {
  const { t } = useLingui();
  const controlRate = successRate(exp.control_success, exp.control_total);
  const treatmentRate = successRate(exp.treatment_success, exp.treatment_total);
  const effectSize = treatmentRate - controlRate;
  const totalSamples = exp.control_total + exp.treatment_total;
  const targetSamples = Math.max(1, exp.min_sample_size * 2);
  const sampleProgress = Math.min(100, (totalSamples / targetSamples) * 100);

  const effectIcon =
    effectSize > 0.001 ? (
      <TrendingUp className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
    ) : effectSize < -0.001 ? (
      <TrendingDown className="h-3.5 w-3.5 text-red-600" aria-hidden />
    ) : (
      <Minus className="h-3.5 w-3.5 text-ink-3" aria-hidden />
    );

  const effectColor =
    effectSize > 0.001 ? "text-emerald-600" : effectSize < -0.001 ? "text-red-600" : "text-ink-3";

  return (
    <article className="rounded-xl border border-hairline border-l-4 border-l-ando-orange bg-cream/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="truncate font-semibold text-base text-ink-1">{exp.name}</h4>
          <p className="mt-0.5 line-clamp-2 text-ink-2 text-sm">
            <span className="font-medium">{exp.specialist_name}</span>
            {exp.hypothesis ? (
              <>
                <span className="mx-1 text-ink-3">·</span>
                <span className="text-ink-3">{exp.hypothesis}</span>
              </>
            ) : null}
          </p>
        </div>
        <Badge variant="outline" className="shrink-0">
          {exp.status}
        </Badge>
      </div>

      {/* Sample-size progress */}
      <div className="mt-4">
        <div className="mb-1 flex justify-between text-ink-3 text-xs">
          <span>
            <Trans>Dimensione campione</Trans>
          </span>
          <span className="tabular-nums">
            {formatInt(totalSamples)} / {formatInt(targetSamples)}
          </span>
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-ink-3/10"
          role="progressbar"
          aria-valuenow={Math.round(sampleProgress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t`Progresso dimensione campione`}
        >
          <div
            className="h-full rounded-full bg-ando-orange"
            style={{ width: `${sampleProgress}%` }}
          />
        </div>
      </div>

      {/* Control vs Treatment */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <ArmCard
          title={t`Control`}
          rate={controlRate}
          samples={exp.control_total}
          traffic={trafficFraction(exp.control_traffic_pct)}
        />
        <ArmCard
          title={t`Treatment`}
          rate={treatmentRate}
          samples={exp.treatment_total}
          traffic={trafficFraction(exp.treatment_traffic_pct)}
          highlight={effectSize > EFFECT_THRESHOLD}
        />
      </div>

      {/* Effect size + significance prediction */}
      <div className="mt-4 space-y-1.5 rounded-xl bg-ink-3/5 p-3 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-ink-3">
            <Trans>Effect size</Trans>
          </span>
          <span className={cn("flex items-center gap-1 font-medium tabular-nums", effectColor)}>
            {effectIcon}
            {formatPct(effectSize * 100, 2)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-3">
            <Trans>Significatività attesa</Trans>
          </span>
          <SignificancePrediction
            cSucc={exp.control_success}
            cTot={exp.control_total}
            tSucc={exp.treatment_success}
            tTot={exp.treatment_total}
            effect={effectSize}
          />
        </div>
        <p className="border-hairline border-t pt-1 text-ink-3 leading-snug">
          <Trans>
            Soglia: α=0.05 (χ² ≥ {CHI_SQ_CRITICAL_005}), effect ≥{" "}
            {formatPct(EFFECT_THRESHOLD * 100, 0)}. Auto-promozione al raggiungimento di entrambe.
          </Trans>
        </p>
        <p className="text-ink-3">
          <Trans>Creato il {formatTimestamp(exp.created_at)}</Trans>
        </p>
      </div>
    </article>
  );
}

export function HmExperimentsTab({
  tenantId,
}: {
  tenantId: string;
}): React.ReactElement {
  const { data, isLoading, isError, refetch } = useHypermemExperiments(tenantId);

  const experiments = data?.experiments ?? [];

  return (
    <div className="space-y-4">
      <header className="rounded-xl border border-hairline bg-cream/40 p-4">
        <h3 className="flex items-center gap-2 font-semibold text-base text-ink-1">
          <Beaker className="h-4 w-4 text-ando-orange" aria-hidden />
          <Trans>A/B esperimenti attivi</Trans>
        </h3>
        <p className="mt-1 text-ink-3 text-sm">
          <Trans>
            Il MetaPrompt agent confronta le versioni dei prompt via chi-square test (α=0.05, effect
            ≥5%). Il job EXPERIMENT_MONITOR auto-promuove il vincitore se statisticamente
            significativo. χ² calcolato lato client come anteprima; il backend è l'unico autorizzato
            a promuovere.
          </Trans>
        </p>
      </header>

      {isLoading && <HmLoading label={<Trans>Caricamento esperimenti…</Trans>} />}

      {isError && (
        <HmError
          message={<Trans>Impossibile caricare gli esperimenti.</Trans>}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && experiments.length === 0 && (
        <HmEmpty
          icon={<Beaker className="h-8 w-8 opacity-50" aria-hidden />}
          label={<Trans>Nessun esperimento attivo</Trans>}
        />
      )}

      {!isLoading && !isError && experiments.length > 0 && (
        <div className="space-y-4">
          {experiments.map((exp) => (
            <ExperimentCard key={exp.id} exp={exp} />
          ))}
        </div>
      )}
    </div>
  );
}
